import { useCallback, useEffect, useId, useRef, useState } from 'react'

import { useAuth } from '../auth/use-auth'
import { displayName } from '../auth/claims'
import { FOCUS_RING_CLASS } from './focus-ring'

/**
 * Staff identity menu in the top-right of the AppShell.
 *
 * The 30px accent circle is the avatar in the MUR mockups; the staff name and
 * role rendered beside it are static text owned by `HeaderBar`. Making the
 * avatar the menu trigger keeps sign-out reachable without adding a control
 * the mockups do not show, and it is the only target in that cluster.
 *
 * Initials come from the signed-in staff name - Suits Me employee identity,
 * not member PII. See the note in `header-bar.tsx`.
 *
 * Two measured values worth not re-deriving. White on `--acc` is 4.74:1, so
 * the initials clear AA for text below 18.66px but with no headroom: do not
 * lighten the accent and do not tint the label. And the circle is 30px
 * against the 32x32 house minimum on MUR Design Tokens and Accessibility -
 * it clears WCAG 2.2 AA (2.5.8 asks 24x24) but not the house rule, and the
 * mockups specify 30px. Raised with `ux-analyst`; do not silently pick a side.
 *
 * Menu behaviour, per the `UserMenu` contract in `doc/design-system.md`:
 *   - Trigger shows the user initials (computed from `name`).
 *   - Menu opens on Enter/Space; Esc closes; arrow keys cycle items;
 *     focus returns to the trigger on close (focus trap is light - we
 *     have a single menu item, so a full trap is overkill).
 *   - `aria-haspopup="menu"` + `aria-expanded`; menu has `role="menu"`,
 *     items have `role="menuitem"`.
 *
 * The menu carries one item at M1: "Sign out". The signed-in-as label is
 * shown statically inside the popover (not as an actionable item).
 */
export function SignOutMenu(): JSX.Element | null {
  const { claims, signOut, signedIn } = useAuth()
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const itemRef = useRef<HTMLButtonElement | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [open, setOpen] = useState(false)
  const id = useId()
  const menuId = `${id}-menu`

  // Close on outside click / Esc.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false)
        triggerRef.current?.focus()
      }
    }
    const onClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onClick)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onClick)
    }
  }, [open])

  // When opening, move focus to the first menu item.
  useEffect(() => {
    if (open) itemRef.current?.focus()
  }, [open])

  const handleSignOut = useCallback(() => {
    void signOut()
  }, [signOut])

  if (!signedIn) return null

  const name = displayName(claims)
  const initials = computeInitials(name)

  return (
    <div ref={containerRef} className="relative inline-flex">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={`Signed in as ${name}. Open user menu.`}
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex h-[30px] w-[30px] items-center justify-center rounded-full bg-brand-primary text-murmuration-label font-semibold text-text-on-brand ${FOCUS_RING_CLASS}`}
      >
        {initials}
      </button>
      {open && (
        <div
          role="menu"
          id={menuId}
          aria-label="User menu"
          className="absolute right-0 top-full z-20 mt-murmuration-2 w-64 rounded-md bg-bg-surface py-murmuration-2 shadow-elev-2 ring-1 ring-border-default"
        >
          <div className="px-murmuration-3 pb-murmuration-2 text-murmuration-label text-text-muted">
            Signed in as
          </div>
          <div className="px-murmuration-3 pb-murmuration-3 text-murmuration-body-strong text-text-primary">
            {name}
          </div>
          <div className="my-murmuration-1 border-t border-border-default" />
          <button
            ref={itemRef}
            type="button"
            role="menuitem"
            onClick={handleSignOut}
            className={`block w-full px-murmuration-3 py-murmuration-2 text-left text-murmuration-body text-text-primary hover:bg-bg-surface-2 ${FOCUS_RING_CLASS}`}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  )
}

function computeInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean).slice(0, 2)
  if (parts.length === 0) return '?'
  return parts.map((p) => p[0]?.toUpperCase() ?? '').join('')
}
